// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { Tag } from '@carbon/react';
import {
  getNumberAttr,
  getPlanTags,
  getStringAttr,
  parseExecutionStart,
} from '../../../utils/planAttributes';
import { getExecutionWindow } from '../../../utils/planLifecycle';
import { useSiteTime } from '../../../hooks/useSiteTime';
import { formatInZone } from '../../../utils/siteTime';
import type { HitlApproval, PlanPhase } from '../../../types';

/** `JUL 28` — a calendar day, so read in UTC rather than converted. */
function formatExecutionDay(iso: string | undefined): string | undefined {
  const date = parseExecutionStart(iso);
  if (!date) return undefined;
  return formatInZone(date, 'dateShort', 'UTC', '').toUpperCase() || undefined;
}

/** `2:00–9:00 PM` from a phase's own `HH:MM` window. */
function formatPhaseWindow(phase: PlanPhase | undefined): string | undefined {
  const start = phase?.time_window?.start;
  const end = phase?.time_window?.end;
  if (!start || !end) return undefined;
  return `${start}–${end}`;
}

function Stat({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="hs-card-stat">
      <span className="hs-card-stat-label">{label}</span>
      <span className="hs-card-stat-value hs-card-stat-value-savings">{value}</span>
      {note && <span className="hs-card-stat-note">{note}</span>}
    </div>
  );
}

export interface LivePlanHeroProps {
  approval: HitlApproval;
  /** The phase whose window contains `now`, when the narration has phases. */
  activePhase?: PlanPhase;
}

/**
 * The "executing now" banner. Two figures differ from the mockup because the
 * data does: the badge shows the *active phase's* window (only
 * `phases[].time_window` carries a real intraday range), and the note reads
 * "<n> kWh vs. baseline" — a whole-day total — until upstream emits the
 * peak-shift figure the mockup implies.
 */
export default function LivePlanHero({ approval, activePhase }: LivePlanHeroProps) {
  const { format } = useSiteTime();
  const attributes = approval.attributes;
  const tags = getPlanTags(attributes);
  const title = getStringAttr(attributes, 'title');
  const day = formatExecutionDay(getStringAttr(attributes, 'executionStart'));
  const phaseWindow = formatPhaseWindow(activePhase);
  const generatedOn = format(approval.createdAt, 'dateTime', '');
  const approvedOn = format(approval.updatedAt ?? approval.createdAt, 'dateTime', '');
  const window = getExecutionWindow(attributes);

  const energyPct = getNumberAttr(attributes, 'energyVsBaselinePct');
  const energyKwh = getNumberAttr(attributes, 'energyVsBaselineKwh');
  const costSavings = getNumberAttr(attributes, 'totalCostSavings');

  return (
    // Not `hs-card hs-card-plan`: those carry a fixed 362px width, a pointer
    // cursor and a hover state, all of which belong to the clickable card grids
    // on Home/Past/Tomorrow. This banner is neither a link nor a fixed-width
    // tile, so it owns its box and borrows only the typography classes.
    <div className="live-hero">
      <div className="hs-card-row">
        <span className="hs-card-badge live-hero-badge">
          EXECUTING NOW
          {day && ` · ${day}`}
          {phaseWindow && ` · ${activePhase?.name?.toUpperCase() ?? 'PHASE'} ${phaseWindow}`}
        </span>
        <Tag type="green" size="sm" className="live-hero-tag">
          Executing
        </Tag>
      </div>

      {tags.length > 0 && <div className="hs-card-plan-title">{tags.join(' · ')}</div>}
      {title && <p className="hs-card-plan-desc">{title}</p>}

      <div className="hs-card-generated live-hero-dates">
        {generatedOn && <span>Generated on {generatedOn}</span>}
        {approvedOn && <span>Approved on {approvedOn}</span>}
        {window && (
          <span>
            Approved window {format(window.start, 'dateMedium')} →{' '}
            {format(window.end, 'dateMedium')}
          </span>
        )}
      </div>

      {(energyPct !== undefined || costSavings !== undefined) && (
        <div className="hs-card-stats live-hero-stats">
          {energyPct !== undefined && (
            <Stat
              label="Energy saved vs. baseline"
              value={`${Math.abs(energyPct).toLocaleString(undefined, {
                maximumFractionDigits: 1,
              })}%`}
              note={
                energyKwh !== undefined
                  ? `${Math.abs(energyKwh).toLocaleString(undefined, {
                      maximumFractionDigits: 0,
                    })} kWh vs. baseline`
                  : undefined
              }
            />
          )}
          {costSavings !== undefined && (
            <Stat
              label="Expected utility bill savings"
              value={`$${Math.abs(costSavings).toLocaleString(undefined, {
                maximumFractionDigits: 0,
              })}`}
              note="Est · reconciled monthly."
            />
          )}
        </div>
      )}
    </div>
  );
}
